// loop หลักของชีวิต (docs/PLAN.md ข้อ 3.4)
// ทุกฟังก์ชันคืนสถานะใหม่ ไม่แก้ของเดิม และใช้ RNG ที่เก็บในสถานะ จึงเล่นซ้ำได้ผลเดิมจาก seed เดิม

import { MONTH_TH, groundName, migrationNow, type MigrationNow } from "../geo/migration";
import { inRegion } from "../geo/regions";
import { THREATENED } from "../data/iucn";
import { archetypeOf } from "./archetypes";
import { Rng, hashSeed } from "./rng";
import { BASE_DEATH_TEXT, displayName, renderText } from "./text";
import type { Ancestor, DeathCause, GameContent, GameEvent, LifeState, Neighbor, Outcome, SpeciesInfo, StageDef, Stats } from "./types";

export const START_STATS: Stats = { fullness: 70, health: 80, safety: 60 };
const METABOLISM = 9; // ความอิ่มที่ลดต่อ turn ในช่วงวัยที่กินอาหาร
const STARVATION_DAMAGE = 25;
// สุขภาพฟื้นเองทุก turn ถ้ากินอิ่ม ไม่อย่างนั้นแผลเล็กๆ จะสะสมจนตายหมด
const REGEN_WELL_FED = 5; // ความอิ่ม ≥ 60
const REGEN_FED = 2; // ความอิ่ม ≥ 30
// สัดส่วนของการตายก่อนโตที่มาจากความเสี่ยงพื้นฐาน ที่เหลือมาจากเหตุการณ์ ปรับด้วย npm run simulate
const BASE_HAZARD_SHARE = 0.55;

const QUIET_EVENT: GameEvent = {
  id: "quiet",
  weight: 1,
  text: "เวลาผ่านไปอย่างเงียบสงบ",
  choices: [{ label: "ต่อไป", outcomes: [{ chance: 1, text: "ไม่มีอะไรเกิดขึ้น", effects: { health: 3 } }] }],
};

export function stagesOf(species: SpeciesInfo): readonly StageDef[] {
  return archetypeOf(species).stages(species);
}

export function currentStage(state: LifeState): StageDef {
  return stagesOf(state.species)[state.stageIndex];
}

function stageDaysPerTurn(species: SpeciesInfo, stage: StageDef): number {
  const days = stage.id === "egg" && species.traits.eggDays ? species.traits.eggDays : species.traits.lifespanYears * 365 * stage.lifeShare;
  return days / stage.turns;
}

const neighborName = (n: Neighbor) => n.nameTh ?? n.sci;

/** สถานะการอพยพตอนนี้ (null = ไม่ใช่สัตว์อพยพ หรือยังไม่มีตำแหน่งบนแผนที่) */
export function migrationStatus(state: LifeState): MigrationNow | null {
  if (!state.migration || !state.geo) return null;
  const stage = currentStage(state);
  const now = migrationNow(state.species.sci, state.migration, state.geo.at, state.ageDays, stageDaysPerTurn(state.species, stage), { tag: stage.tag, turnInStage: state.turnInStage });
  // ไข่เดินทางเองไม่ได้ แมลงต้องรอโตเป็นตัวเต็มวัยที่มีปีกก่อน (หนอนผีเสื้อไม่อพยพ)
  const grounded = stage.tag === "dormant" || (state.species.archetype === "INSECT" && stage.tag !== "adult" && stage.tag !== "old");
  return now && grounded ? { ...now, due: false } : now;
}

function migrationVars(state: LifeState): Record<string, string> {
  const now = migrationStatus(state);
  if (!now) return {};
  // turn ยาวกว่าฤดู เดือนตามอายุไม่มีความหมาย ใช้เดือนที่ออกเดินทางจริงของเส้นทางนั้นแทน
  const [start, end] = now.route.aMonths;
  const month = !now.long ? now.month : now.target === "b" ? (end % 12) + 1 : start;
  return { destination: groundName(now.route, now.target), month: MONTH_TH[month - 1], migrationFact: now.migrant.fact };
}

function textVars(state: LifeState, extra: Record<string, string | number> = {}) {
  const enc = state.encounter;
  return {
    ...migrationVars(state),
    name: displayName(state.species),
    stage: currentStage(state).nameTh,
    predator: enc?.role === "predator" ? neighborName(enc.who) : "นักล่า",
    prey: enc?.role === "prey" ? neighborName(enc.who) : "เหยื่อ",
    ...extra,
  };
}

const neighborList = (state: LifeState, role: "predator" | "prey") =>
  (role === "predator" ? state.neighbors?.predators : state.neighbors?.prey) ?? [];

function addLog(state: LifeState, text: string, extra?: Record<string, string | number>): void {
  state.log.push({
    turn: state.turn,
    ageDays: state.ageDays,
    stageId: currentStage(state).id,
    text: renderText(text, textVars(state, extra)),
  });
}

const clamp = (v: number) => Math.max(0, Math.min(100, v));

function eventMatches(event: GameEvent, state: LifeState, stage: StageDef): boolean {
  const { species } = state;
  if (event.archetypes && !event.archetypes.includes(species.archetype)) return false;
  if (event.stages && !event.stages.includes(stage.id)) return false;
  if (event.stageTags && !event.stageTags.includes(stage.tag)) return false;
  if (event.once && state.seenEvents.includes(event.id)) return false;
  if (event.opening && state.turnInStage !== 0) return false;
  const r = event.requires;
  if (!r) return true;
  if (r.canFly !== undefined && r.canFly !== species.traits.canFly) return false;
  if (r.sessile !== undefined && r.sessile !== (species.traits.sessile ?? false)) return false;
  if (r.broadcastSpawn !== undefined && r.broadcastSpawn !== (species.traits.broadcastSpawn ?? false)) return false;
  if (r.reproduction && r.reproduction !== species.traits.reproduction) return false;
  if (r.diet && !r.diet.includes(species.traits.diet)) return false;
  if (r.habitat && !r.habitat.some((h) => species.traits.habitat.includes(h))) return false;
  if (r.subgroups && !r.subgroups.includes(species.subgroupId)) return false;
  if (r.species && !r.species.includes(species.sci)) return false;
  if (r.notSubgroups?.includes(species.subgroupId)) return false;
  if (r.families && !(species.family && r.families.includes(species.family))) return false;
  if (r.genera && !r.genera.includes(species.sci.split(" ")[0])) return false;
  if (r.notFamilies && species.family && r.notFamilies.includes(species.family)) return false;
  if (r.flags && !r.flags.every((f) => state.flags.includes(f))) return false;
  if (r.notFlags && r.notFlags.some((f) => state.flags.includes(f))) return false;
  if (r.encounter && !neighborList(state, r.encounter).length) return false;
  if (r.migration) {
    const now = migrationStatus(state);
    if (r.migration === "resident") {
      if (now) return false;
    } else if (!now) return false;
    else if (r.migration.startsWith("due") && (!now.due || (r.migration !== "due" && r.migration !== `due_${now.target}`))) return false;
    else if (r.migration.startsWith("at") && (now.here !== now.target || r.migration !== `at_${now.target}`)) return false;
  }
  if (r.biome) {
    const geo = state.geo;
    if (!geo || !r.biome.some((b) => b === geo.biome || (b === "mangrove" && geo.mangrove))) return false;
  }
  if (r.region) {
    const geo = state.geo;
    if (!geo || !r.region.some((id) => inRegion(id, geo.at, geo.onSea))) return false;
  }
  return true;
}

export function availableEvents(state: LifeState, content: GameContent): GameEvent[] {
  const stage = currentStage(state);
  return content.events.filter((e) => eventMatches(e, state, stage));
}

function eventWeight(event: GameEvent, species: SpeciesInfo): number {
  const threatened = species.iucn !== null && THREATENED.has(species.iucn);
  return event.weight * (threatened && event.threatenedWeight ? event.threatenedWeight : 1);
}

function findEvent(id: string, content: GameContent): GameEvent {
  return id === QUIET_EVENT.id ? QUIET_EVENT : content.events.find((e) => e.id === id)!;
}

const specificity = (e: GameEvent) => (e.requires?.species ? 3 : e.requires?.families || e.requires?.genera ? 2 : e.requires?.subgroups ? 1 : 0);

/** ฉากเปิดที่เจาะจงที่สุดก่อน ไม่มีฉากเปิดก็สุ่มตามน้ำหนัก โดยเลี่ยงเหตุการณ์เดียวกับ turn ก่อน */
function candidateEvents(state: LifeState, content: GameContent): GameEvent[] {
  const events = availableEvents(state, content);
  const openings = events.filter((e) => e.opening);
  if (openings.length) {
    const top = Math.max(...openings.map(specificity));
    return openings.filter((e) => specificity(e) === top);
  }
  // เหตุการณ์ออกเดินทาง: ชนิดที่มีของตัวเอง (เช่น ปลาแซลมอน วาฬ) ใช้ของตัวเองแทนเหตุการณ์ทั่วไป
  const isDue = (e: GameEvent) => !!e.requires?.migration?.startsWith("due");
  const dueTop = Math.max(-1, ...events.filter(isDue).map(specificity));
  const usable = events.filter((e) => !isDue(e) || specificity(e) === dueTop);
  const fresh = usable.filter((e) => e.id !== state.lastEvent);
  // ถึงฤดูอพยพแล้วต้องได้ตัดสินใจ (ถ้าเพิ่งเลือกรอ turn นี้ค่อยเป็นเหตุการณ์อื่น)
  // สัตว์อายุยืนที่ turn ยาวกว่าฤดู ถึงเวลาเดินทางทุก turn จึงไม่บังคับ ให้สุ่มตามน้ำหนักแทน
  const due = migrationStatus(state)?.long ? [] : fresh.filter(isDue);
  if (due.length && !events.some((e) => isDue(e) && e.id === state.lastEvent)) return due;
  return fresh.length ? fresh : usable;
}

/** เริ่ม turn: เวลาผ่านไป แล้วสุ่มเหตุการณ์ของ turn นี้ */
function beginTurn(state: LifeState, content: GameContent, rng: Rng): void {
  const stage = currentStage(state);
  if (stage.feeds) state.stats.fullness = clamp(state.stats.fullness - METABOLISM);

  const events = candidateEvents(state, content);
  const event = events.length ? events[rng.weightedIndex(events.map((e) => eventWeight(e, state.species)))] : QUIET_EVENT;
  state.pendingEvent = event.id;
  // เหตุการณ์ที่ต้องมีนักล่าหรือเหยื่อ เลือกตัวจริงที่อยู่แถวนี้มาหนึ่งชนิด
  delete state.encounter;
  const role = event.requires?.encounter;
  if (role) {
    const list = neighborList(state, role);
    state.encounter = { role, who: list[rng.int(0, list.length - 1)] };
  }
}

function die(state: LifeState, cause: DeathCause, text?: string): void {
  if (text) addLog(state, text);
  state.alive = false;
  state.causeOfDeath = cause;
  state.pendingEvent = null;
}

function baseHazardCause(state: LifeState, rng: Rng): Exclude<DeathCause, "starvation" | "old_age" | "sacrifice"> {
  const young = currentStage(state).tag !== "adult" && currentStage(state).tag !== "old";
  const threatened = state.species.iucn !== null && THREATENED.has(state.species.iucn);
  // สัตว์กินเนื้อที่โตแล้วมักอยู่บนสุดของห่วงโซ่อาหาร จึงไม่ค่อยตายเพราะถูกล่า
  const predatorWeight = young ? 6 : state.species.traits.diet === "carnivore" ? 0.5 : 3;
  const causes = ["predator", "disease", "accident", "disaster", "human"] as const;
  const weights = [predatorWeight, 2, 1, 0.5, threatened ? 2 : 0.5];
  return causes[rng.weightedIndex(weights)];
}

function baseHazard(state: LifeState): number {
  const def = archetypeOf(state.species);
  const stages = stagesOf(state.species);
  const stage = stages[state.stageIndex];
  let hazard: number;
  if (stage.tag === "old") {
    hazard = def.oldHazard;
  } else if (stage.tag === "adult") {
    // archetype ที่ไม่มีวัยชรา ความเสี่ยงจะเพิ่มขึ้นใน 4 turn สุดท้ายของวัยเจริญพันธุ์
    const last = state.stageIndex === stages.length - 1 && stage.turns - state.turnInStage <= 4;
    hazard = last ? def.oldHazard : def.adultHazard;
  } else {
    const adultIndex = stages.findIndex((s) => s.tag === "adult");
    const preAdultTurns = stages.slice(0, adultIndex).reduce((n, s) => n + s.turns, 0);
    hazard = (1 - Math.pow(def.survivalToAdult, 1 / preAdultTurns)) * BASE_HAZARD_SHARE * (def.youngHazardScale ?? 1);
  }
  // ลูกของคู่ที่แข็งแรงรอดง่ายกว่าตอนยังเล็ก
  if (state.flags.includes("sturdy") && stage.tag !== "adult" && stage.tag !== "old") hazard *= STURDY_HAZARD;
  // ความปลอดภัยสูงลดความเสี่ยง ความปลอดภัยต่ำเพิ่มความเสี่ยง
  const safetyFactor = Math.max(0.5, Math.min(1.6, 1.6 - state.stats.safety / 100));
  return hazard * safetyFactor;
}

function clutchCount(species: SpeciesInfo, rng: Rng): number {
  const base = species.traits.clutchSize;
  return Math.max(1, Math.round(base * (0.6 + rng.next() * 0.8)));
}

function applyOutcome(state: LifeState, outcome: Outcome, rng: Rng): void {
  const e = outcome.effects ?? {};
  let born = e.offspringCount ?? 0;
  if (e.offspring === "clutch") born += clutchCount(state.species, rng);
  state.offspring += born;
  addLog(state, outcome.text, { n: born });
  if (e.mate) state.mate = e.mate;
  if (born > 0 && !e.offspringCount) {
    // จำที่ออกลูกและคู่ไว้ ให้เล่นต่อเป็นลูกตัวนี้ได้
    state.lastBrood = { ...(state.geo ? { at: state.geo.at } : {}), ...(state.mate ? { mate: state.mate } : {}) };
    delete state.mate;
  }

  state.stats.fullness = clamp(state.stats.fullness + (e.fullness ?? 0));
  state.stats.health = clamp(state.stats.health + (e.health ?? 0));
  state.stats.safety = clamp(state.stats.safety + (e.safety ?? 0));
  if (e.move === "migrate") {
    // จำไว้ว่ากำลังไปแหล่งไหน เพราะจบ turn แล้วเดือนอาจเปลี่ยน
    const now = migrationStatus(state);
    if (now && state.migration) {
      state.move = "migrate";
      state.migration = { ...state.migration, heading: now.target };
    }
  } else if (e.move) state.move = e.move;
  if (e.removeFlags) state.flags = state.flags.filter((f) => !e.removeFlags!.includes(f));
  if (e.addFlags) for (const f of e.addFlags) if (!state.flags.includes(f)) state.flags.push(f);

  const cause = e.death ?? (state.stats.health <= 0 ? e.cause ?? "accident" : null);
  if (cause === "predator") {
    // รู้ชื่อผู้ล่าถ้าเป็นตัวที่เจอในเหตุการณ์ หรือสุ่มจากนักล่าแถวนี้
    const predators = neighborList(state, "predator");
    if (state.encounter?.role === "predator") state.killer = state.encounter.who;
    else if (predators.length) state.killer = predators[rng.int(0, predators.length - 1)];
  }
  if (cause) die(state, cause);
}

function endTurn(state: LifeState, rng: Rng): void {
  const stage = currentStage(state);
  const dormant = stage.tag === "dormant";

  if (stage.feeds && state.stats.fullness <= 0) {
    state.starvingTurns++;
    state.stats.health = clamp(state.stats.health - STARVATION_DAMAGE);
    addLog(state, "คุณหิวจนตัวสั่น ร่างกายเริ่มอ่อนแรง");
    if (state.stats.health <= 0) return die(state, "starvation", "คุณไม่มีแรงเหลือพอจะหาอาหารได้อีกแล้ว");
  } else {
    state.starvingTurns = 0;
    const fed = !stage.feeds || state.stats.fullness >= 60 ? REGEN_WELL_FED : state.stats.fullness >= 30 ? REGEN_FED : 0;
    state.stats.health = clamp(state.stats.health + fed);
  }

  if (rng.chance(baseHazard(state))) {
    const cause = baseHazardCause(state, rng);
    const predators = neighborList(state, "predator");
    if (cause === "predator" && predators.length) {
      // รู้ว่าแถวนี้มีนักล่าชนิดไหน จึงบอกชื่อผู้ล่าได้
      const who = predators[rng.int(0, predators.length - 1)];
      state.killer = who;
      const name = neighborName(who);
      return die(state, cause, dormant ? `${name}มาพบคุณเข้า และคุณกลายเป็นอาหารมื้อเล็กๆ ของมัน` : `${name}เห็นคุณก่อนที่คุณจะเห็นมัน และทุกอย่างจบลงอย่างรวดเร็ว`);
    }
    return die(state, cause, dormant ? BASE_DEATH_TEXT[cause].dormant : BASE_DEATH_TEXT[cause].active);
  }

  state.ageDays += stageDaysPerTurn(state.species, stage);
  state.turn++;
  state.turnInStage++;
  if (state.turnInStage < stage.turns) return;

  const stages = stagesOf(state.species);
  if (state.stageIndex === stages.length - 1) {
    return die(state, "old_age", "คุณใช้ชีวิตมาจนสุดทางแล้ว และหลับตาลงอย่างสงบ");
  }
  state.stageIndex++;
  state.turnInStage = 0;
  const next = stages[state.stageIndex];
  addLog(state, next.enterText ?? "คุณเติบโตเข้าสู่ช่วง{stage}");
}

function withRng(state: LifeState, fn: (draft: LifeState, rng: Rng) => void): LifeState {
  const draft = structuredClone(state);
  const rng = new Rng(draft.rng);
  fn(draft, rng);
  draft.rng = rng.state;
  return draft;
}

export function startLife(species: SpeciesInfo, seed: string, content: GameContent): LifeState {
  const initial: LifeState = {
    seed,
    rng: hashSeed(`${seed}:${species.id}`),
    species,
    stageIndex: 0,
    turnInStage: 0,
    turn: 0,
    ageDays: 0,
    stats: { ...START_STATS },
    flags: [],
    seenEvents: [],
    offspring: 0,
    starvingTurns: 0,
    pendingEvent: null,
    alive: true,
    causeOfDeath: null,
    log: [],
  };
  return withRng(initial, (s, rng) => {
    const first = currentStage(s);
    addLog(s, first.id === "egg" ? "คุณเริ่มต้นชีวิตเป็นไข่ของ{name}" : (first.enterText ?? "คุณลืมตาดูโลกเป็น{name}"));
    const start = archetypeOf(species).startFlags?.(species, rng);
    if (start) s.flags.push(...start);
    beginTurn(s, content, rng);
  });
}

export const STURDY_HAZARD = 0.8;
const PROVIDER_FULLNESS = 20;
const MAX_ANCESTORS = 12;
const MATE_TEXT = {
  strong: "พ่อแม่ของคุณเลือกคู่ที่แข็งแรงที่สุด คุณจึงเกิดมาแข็งแรงกว่าตัวอื่น",
  provider: "พ่อแม่ของคุณเลือกคู่ที่หาอาหารเก่ง คุณจึงได้กินอิ่มตั้งแต่แรก",
  near: null,
} as const;

/** เล่นต่อได้ไหม ต้องมีลูกของตัวเองจริง (ไม่นับน้องที่มดงานช่วยเลี้ยง) */
export const canContinueAsChild = (parent: LifeState) => !parent.alive && parent.offspring > 0 && !!parent.lastBrood;

export function ancestorOf(s: LifeState): Ancestor {
  return {
    seed: s.seed,
    ageDays: Math.round(s.ageDays),
    offspring: s.offspring,
    stageId: currentStage(s).id,
    cause: s.causeOfDeath,
    ...(s.killer?.nameTh ? { killer: s.killer.nameTh } : {}),
    ...(s.lastBrood?.mate ? { mate: s.lastBrood.mate } : {}),
  };
}

/** seed ของลูก ได้จาก seed ของพ่อแม่ เล่นซ้ำได้ผลเดิม และใช้ในลิงก์แชร์ได้ */
export const childSeed = (parent: LifeState) => `${hashSeed(`${parent.seed}:child`).toString(36)}${(parent.lineage?.generation ?? 1) + 1}`.slice(0, 24);

/** เกิดใหม่เป็นลูกของชีวิตที่จบไป: ชนิดเดิม เกิดที่ที่พ่อแม่ออกลูก ได้ผลจากคู่ที่พ่อแม่เลือก */
export function startChildLife(parent: LifeState, content: GameContent): LifeState {
  if (!canContinueAsChild(parent)) throw new Error("ชีวิตนี้ไม่มีลูกให้เล่นต่อ");
  const generation = (parent.lineage?.generation ?? 1) + 1;
  const child = startLife(parent.species, childSeed(parent), content);
  const brood = parent.lastBrood!;
  child.lineage = { generation, ancestors: [...(parent.lineage?.ancestors ?? []), ancestorOf(parent)].slice(-MAX_ANCESTORS) };
  if (brood.at) child.birthAt = brood.at;
  const intro = { turn: 0, ageDays: 0, stageId: child.log[0].stageId };
  const extra = [{ ...intro, text: `คุณเป็นลูกรุ่นที่ ${generation} ของสายตระกูลนี้` }];
  if (brood.mate === "strong") child.flags.push("sturdy");
  if (brood.mate === "provider") child.stats.fullness = clamp(child.stats.fullness + PROVIDER_FULLNESS);
  const mateText = brood.mate && MATE_TEXT[brood.mate];
  if (mateText) extra.push({ ...intro, text: mateText });
  child.log = [child.log[0], ...extra, ...child.log.slice(1)];
  return child;
}

export interface PresentedEvent {
  id: string;
  text: string;
  fact?: string;
  choices: string[];
  signature?: boolean; // เหตุการณ์เฉพาะตัวของชนิดนี้
}

/** เหตุการณ์ที่เจอสัตว์ชนิดจริง บอกชื่อวิทยาศาสตร์และข้อเท็จจริงของชนิดนั้น */
function encounterFact(state: LifeState): string | undefined {
  const who = state.encounter?.who;
  if (!who) return undefined;
  return `${neighborName(who)} (${who.sci})${who.note ? ` ${who.note}` : ""}`;
}

export function presentEvent(state: LifeState, content: GameContent): PresentedEvent | null {
  if (!state.alive || !state.pendingEvent) return null;
  const event = findEvent(state.pendingEvent, content);
  return {
    id: event.id,
    text: renderText(event.text, textVars(state)),
    fact: event.fact ? renderText(event.fact, textVars(state)) : encounterFact(state),
    choices: event.choices.map((c) => renderText(c.label, textVars(state))),
    ...(event.requires?.species ? { signature: true } : {}),
  };
}

/** ผู้เล่นเลือกตัวเลือก: สุ่มผลลัพธ์ จบ turn แล้วเริ่ม turn ถัดไป */
export function choose(state: LifeState, choiceIndex: number, content: GameContent): LifeState {
  if (!state.alive || !state.pendingEvent) throw new Error("ชีวิตนี้จบแล้ว หรือยังไม่มีเหตุการณ์ให้เลือก");
  const event = findEvent(state.pendingEvent, content);
  const choice = event.choices[choiceIndex];
  if (!choice) throw new RangeError(`ไม่มีตัวเลือกที่ ${choiceIndex}`);

  return withRng(state, (s, rng) => {
    if (!s.seenEvents.includes(event.id)) s.seenEvents.push(event.id);
    s.lastEvent = event.id;
    s.pendingEvent = null;
    delete s.move;
    applyOutcome(s, choice.outcomes[rng.weightedIndex(choice.outcomes.map((o) => o.chance))], rng);
    if (!s.alive) return;
    endTurn(s, rng);
    if (s.alive) beginTurn(s, content, rng);
  });
}

export interface LifeSummary {
  ageDays: number;
  turns: number;
  offspring: number;
  cause: DeathCause | null;
  reachedAdult: boolean;
  stageId: string;
}

export function summarize(state: LifeState): LifeSummary {
  const stages = stagesOf(state.species);
  const adultIndex = stages.findIndex((s) => s.tag === "adult");
  return {
    ageDays: state.ageDays,
    turns: state.turn,
    offspring: state.offspring,
    cause: state.causeOfDeath,
    reachedAdult: state.stageIndex >= adultIndex,
    stageId: stages[state.stageIndex].id,
  };
}
